package com.corbettcode.allaboutrocks

import com.varabyte.kobweb.compose.css.ScrollBehavior
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.ButtonStyle
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.layout.HorizontalDividerStyle
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.init.registerStyleBase
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.addVariantBase
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import com.varabyte.kobweb.silk.theme.modifyStyleBase
import org.jetbrains.compose.web.css.*

// --- Constants ---
val HERO_IMAGE_MAX_WIDTH = 600.px
val FIGURE_IMAGE_MAX_WIDTH = 600.px
val BOOK_COVER_MAX_WIDTH = 400.px

@InitSilk
fun initSiteStyles(ctx: InitSilkContext) {
    // This site does not need scrolling itself, but this is a good demonstration for how you might enable this in your
    // own site. Note that we only enable smooth scrolling unless the user has requested reduced motion, which is
    // considered a best practice.
    ctx.stylesheet.registerStyle("html") {
        cssRule(CSSMediaQuery.MediaFeature("prefers-reduced-motion", StylePropertyValue("no-preference"))) {
            Modifier.scrollBehavior(ScrollBehavior.Smooth)
        }
    }

    ctx.stylesheet.registerStyleBase("body") {
        Modifier
            .fontFamily(
                "-apple-system", "BlinkMacSystemFont", "Segoe UI", "Roboto", "Oxygen", "Ubuntu",
                "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", "sans-serif"
            )
            .fontSize(18.px)
            .lineHeight(1.5)
    }

    // Silk dividers only extend 90% by default; we want full width dividers in our site
    ctx.theme.modifyStyleBase(HorizontalDividerStyle) {
        Modifier.fillMaxWidth()
    }
}

val HeadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(3.cssRem)
        .textAlign(TextAlign.Start)
        .lineHeight(1.2) //1.5x doesn't look as good on very large text
}

val SubheadlineTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .textAlign(TextAlign.Start)
        .color(
            colorMode
                .toPalette()
                .color
                .toRgb()
                .copyf(alpha = 0.8f)
        )
}

val SectionTextStyle = CssStyle.base {
    Modifier
        .fontSize(1.cssRem)
        .textAlign(TextAlign.Start)
        .color(
            colorMode
                .toPalette()
                .color
                .toRgb()
                .copyf(alpha = 0.8f)
        )
}

val CircleButtonVariant = ButtonStyle.addVariantBase {
    Modifier.padding(0.px).borderRadius(50.percent)
}

val UncoloredButtonVariant = ButtonStyle.addVariantBase {
    Modifier.setVariable(ButtonVars.BackgroundDefaultColor, Colors.Transparent)
}

/** Attribution line under a figure. Kept small and quiet, but present -- most images here are CC BY or CC BY-SA. */
val ImageCreditStyle = CssStyle {
    base {
        Modifier
            .fontSize(0.75.cssRem)
            .opacity(0.7)
    }
}

// --- Shared article page styles ---
// Content pages (rock types, learn articles) are all laid out the same way, so they share these.

/** The title at the top of a content page. */
val PageHeadlineStyle = CssStyle {
    base { Modifier.fontSize(1.5.cssRem) }          // mobile
    Breakpoint.MD { Modifier.fontSize(2.5.cssRem) } // desktop
}

/** A heading introducing a section within a content page. */
val PageSectionTitleStyle = CssStyle {
    base { Modifier.fontSize(1.0.cssRem) }          // mobile
    Breakpoint.MD { Modifier.fontSize(2.0.cssRem) } // desktop
}

/** A bulleted list inside page copy. */
val PageListStyle = CssStyle {
    base {
        Modifier
            .margin(topBottom = 0.5.cssRem)
            .padding(left = 1.5.cssRem)
    }
}

/** Tagline and carousel sit side by side when there is room; the carousel wraps below on narrow screens */
val HeroContainerStyle = CssStyle {
    base { Modifier.fillMaxWidth().gap(2.cssRem).flexWrap(FlexWrap.Wrap) }
    Breakpoint.MD { Modifier.margin { top(20.vh) } }
}
